import { currentAccountStorageScope, readAccountScopedStorage, writeAccountScopedStorage } from "@/shared/storage/account-scope";
import { retainedAudioOwnerIsCurrent, type RetainedAudioOwner, type RetainedAudioRecording } from "./retained-audio-store";

export type BriefAudioCompletionContext = {
  profileId: string; planIdentity: string; country: string; language: string;
  length: "quick" | "balanced" | "deep"; sectionIds: string[];
};
export type BriefAudioShareExcerpt = { title: string; description: string; source: string };
export type BriefAudioAcceptedStory = { title: string; originalArticleUrl?: string; publishedAtEpochMs: number };
export type BriefAudioCompletion = {
  recordingId: string; completedAtEpochMs: number; context: BriefAudioCompletionContext;
  excerpts: BriefAudioShareExcerpt[];
  acceptedStories?: BriefAudioAcceptedStory[];
};
const KEY = "tb_public_web_brief_audio_completions_v1";
const EVENT = "tb-brief-audio-completions-changed";
const MAX_ROWS = 12;
const AGE_MS = 7 * 86400000;
const text = (value: unknown, maximum: number) => String(value || "").replace(/<[^>]*>/g, " ")
  .replace(/\[\[\s*pause\s*:[^\]]*\]\]/gi, " ").replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim().slice(0, maximum);
function contextValid(value: unknown): value is BriefAudioCompletionContext {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const row = value as BriefAudioCompletionContext;
  return typeof row.profileId === "string" && row.profileId.length > 0 && row.profileId.length <= 200 &&
    typeof row.planIdentity === "string" && row.planIdentity.length > 0 && row.planIdentity.length <= 20000 &&
    ["US", "CA", "AU", "GB", "DE", "FR", "IN", "JP"].includes(row.country) &&
    typeof row.language === "string" && /^[a-z]{2}(?:-[a-z]{2})?$/.test(row.language) &&
    ["quick", "balanced", "deep"].includes(row.length) && Array.isArray(row.sectionIds) &&
    row.sectionIds.length > 0 && row.sectionIds.length <= 40 && new Set(row.sectionIds).size === row.sectionIds.length &&
    row.sectionIds.every((id) => typeof id === "string" && /^[a-z][a-z0-9_]{0,79}$/.test(id));
}
function valid(row: unknown, now: number): row is BriefAudioCompletion {
  if (!row || typeof row !== "object" || Array.isArray(row)) return false;
  const item = row as BriefAudioCompletion;
  return /^[a-f0-9]{64}$/.test(item.recordingId) && Number.isSafeInteger(item.completedAtEpochMs) &&
    item.completedAtEpochMs > 0 && item.completedAtEpochMs <= now && contextValid(item.context) &&
    (item.acceptedStories === undefined || Array.isArray(item.acceptedStories) && item.acceptedStories.length > 0 && item.acceptedStories.length <= 512 && item.acceptedStories.every((entry) => entry &&
      typeof entry.title === "string" && entry.title.length > 0 && entry.title === text(entry.title, 500) &&
      Number.isSafeInteger(entry.publishedAtEpochMs) && entry.publishedAtEpochMs >= 0 && entry.publishedAtEpochMs <= item.completedAtEpochMs + 300000 &&
      (entry.originalArticleUrl === undefined || typeof entry.originalArticleUrl === "string" && /^https?:\/\//.test(entry.originalArticleUrl) && entry.originalArticleUrl.length <= 2048))) &&
    Array.isArray(item.excerpts) && item.excerpts.length <= 3 && item.excerpts.every((entry) => entry &&
      typeof entry.title === "string" && entry.title.length > 0 && entry.title === text(entry.title, 500) &&
      typeof entry.description === "string" && entry.description === text(entry.description, 2000) &&
      typeof entry.source === "string" && entry.source === text(entry.source, 200));
}
function read(now: number): BriefAudioCompletion[] {
  const raw = readAccountScopedStorage(KEY);
  if (!raw) return [];
  if (raw.length > 2 * 1024 * 1024) throw new Error("audio_recap_invalid");
  const data = JSON.parse(raw);
  if (data?.version !== 1 || !Array.isArray(data.rows) || data.rows.length > MAX_ROWS ||
    !data.rows.every((row: unknown) => valid(row, now))) throw new Error("audio_recap_invalid");
  return data.rows.filter((row: BriefAudioCompletion) => row.completedAtEpochMs > now - AGE_MS);
}
export function loadCompletedBriefAudio(now = Date.now()): BriefAudioCompletion[] {
  try { return read(now); } catch { return []; }
}
export function onCompletedBriefAudioChanged(listener: () => void): () => void {
  window.addEventListener(EVENT, listener);
  return () => window.removeEventListener(EVENT, listener);
}

/** Public news excerpts are frozen from the accepted request. Private task,
 * calendar, commute and personal section facts never enter a sharing receipt. */
export function acceptedBriefAudioShareExcerpts(request: Record<string, unknown>): BriefAudioShareExcerpt[] {
  const facts = request.facts as { briefSections?: Array<{ id?: string; articles?: Array<Record<string, unknown>> }> } | undefined;
  const result: BriefAudioShareExcerpt[] = [];
  for (const section of facts?.briefSections || []) {
    if (!section.id?.startsWith("news") || !Array.isArray(section.articles)) continue;
    for (const article of section.articles) {
      const title = text(article.title, 500);
      if (!title || /^(?:news_[a-z0-9_]+|category-[a-z0-9_-]+)$/i.test(title)) continue;
      result.push({ title, description: text(article.description, 2000), source: text(article.publisherName || article.sourceName, 200) });
      if (result.length === 3) return result;
    }
  }
  return result;
}

/** Keep every accepted public news baseline, independently of the three share
 * excerpts. Original URLs join only the exact title/version sent for delivery. */
export function acceptedBriefAudioStories(request: Record<string, unknown>, originals: Array<Record<string, unknown>> = []): BriefAudioAcceptedStory[] | undefined {
  const facts = request.facts as { briefSections?: Array<{ id?: string; articles?: Array<Record<string, unknown>> }> } | undefined;
  const articles = (facts?.briefSections || []).filter((section) => section.id?.startsWith("news"))
    .flatMap((section) => Array.isArray(section.articles) ? section.articles : []);
  if (!articles.length || articles.length > 512) return undefined;
  const version = (article: Record<string, unknown>) => Math.max(0, ...[article.publishedAtEpochMs, article.publishedAtMs, Date.parse(String(article.pubDate || ""))]
    .map(Number).filter((value) => Number.isSafeInteger(value) && value > 0 && value <= Date.now() + 300000));
  const result: BriefAudioAcceptedStory[] = [];
  for (const article of articles) {
    const title = text(article.title, 500);
    if (!title) return undefined;
    const publishedAtEpochMs = version(article);
    const original = originals.slice(0, 1024).find((candidate) => text(candidate.title, 500) === title && version(candidate) === publishedAtEpochMs);
    let originalArticleUrl: string | undefined;
    try {
      const url = new URL(String(original?.originalArticleUrl || original?.link || original?.url || ""));
      if (["http:", "https:"].includes(url.protocol) && !url.username && !url.password && url.href.length <= 2048) originalArticleUrl = url.href;
    } catch { /* A title/version baseline remains available without a URL. */ }
    result.push({ title, ...(originalArticleUrl ? { originalArticleUrl } : {}), publishedAtEpochMs });
  }
  return result;
}

/** Called only by the measured natural full-playback gate after the exact
 * retained recording transaction commits. Replay never calls this function. */
export function recordCompletedBriefAudio(input: {
  owner: RetainedAudioOwner; recording: RetainedAudioRecording; completedAtEpochMs: number;
  context?: BriefAudioCompletionContext; excerpts: BriefAudioShareExcerpt[];
  acceptedStories?: BriefAudioAcceptedStory[];
}): boolean {
  const now = Date.now();
  if (!retainedAudioOwnerIsCurrent(input.owner) || currentAccountStorageScope() !== input.owner.scope ||
    input.recording.kind !== "brief" || input.recording.ownerScope !== input.owner.scope || input.recording.expiresAtEpochMs <= now ||
    !contextValid(input.context) || input.recording.language !== input.context.language) return false;
  const row: BriefAudioCompletion = { recordingId: input.recording.id, completedAtEpochMs: input.completedAtEpochMs,
    context: structuredClone(input.context), excerpts: input.excerpts.map((entry) => ({ ...entry })),
    ...(input.acceptedStories ? { acceptedStories: input.acceptedStories.map((entry) => ({ ...entry })) } : {}) };
  if (!valid(row, now)) return false;
  try {
    const rows = read(now).filter((entry) => entry.recordingId !== row.recordingId);
    rows.push(row); rows.sort((a, b) => b.completedAtEpochMs - a.completedAtEpochMs);
    if (!retainedAudioOwnerIsCurrent(input.owner)) return false;
    const boundedRows = rows.slice(0, MAX_ROWS);
    while (boundedRows.length > 1 && JSON.stringify({ version: 1, rows: boundedRows }).length > 2 * 1024 * 1024) boundedRows.pop();
    const serialized = JSON.stringify({ version: 1, rows: boundedRows });
    if (serialized.length > 2 * 1024 * 1024) return false;
    writeAccountScopedStorage(KEY, serialized);
    if (!retainedAudioOwnerIsCurrent(input.owner)) return false;
    window.dispatchEvent(new Event(EVENT)); return true;
  } catch { return false; }
}
export function matchingCompletedBriefAudio(rows: BriefAudioCompletion[], context: BriefAudioCompletionContext, now = Date.now()): BriefAudioCompletion | null {
  if (!contextValid(context)) return null;
  return rows.filter((row) => valid(row, now) && new Date(row.completedAtEpochMs).toDateString() === new Date(now).toDateString() &&
    row.context.profileId === context.profileId && row.context.planIdentity === context.planIdentity && row.context.country === context.country &&
    row.context.language === context.language && row.context.length === context.length && JSON.stringify(row.context.sectionIds) === JSON.stringify(context.sectionIds))
    .sort((a, b) => b.completedAtEpochMs - a.completedAtEpochMs)[0] || null;
}
